import { useCallback } from "react";
import { useAnalytics } from "@piiqtechnologies/ui-lib";
import { useAuthContext } from "@stores/AuthContext";
import { buildEventName } from "@analytics-setup/events";
/**
 * Returns a taxonomy-enforcing `track(area, action, props)` function.
 *
 * Every event is named `Discover | <Area> | <Action>` and, when the user is
 * known, carries a small set of global properties (role, demo/trial flags, org)
 * so events can be segmented without wiring those props at each call site. This
 * is the app-level stand-in for Mixpanel super properties, which the `analytics`
 * abstraction does not expose directly.
 */
export const useTrackEvent = () => {
    var _a, _b, _c;
    const analytics = useAnalytics();
    const auth = useAuthContext();
    // Read primitives so the callback identity stays stable across renders (the
    // AuthContext value object is re-created each render and is not safe to
    // depend on directly).
    const userId = (_a = auth === null || auth === void 0 ? void 0 : auth.user) === null || _a === void 0 ? void 0 : _a._id;
    const organizationId = (_c = (_b = auth === null || auth === void 0 ? void 0 : auth.user) === null || _b === void 0 ? void 0 : _b.organization) === null || _c === void 0 ? void 0 : _c._id;
    const isDemoUser = auth === null || auth === void 0 ? void 0 : auth.isDemoUser;
    const isViewerUser = auth === null || auth === void 0 ? void 0 : auth.isViewerUser;
    const isDiscoverAdmin = auth === null || auth === void 0 ? void 0 : auth.isDiscoverAdmin;
    const isFreeTrial = auth === null || auth === void 0 ? void 0 : auth.isFreeTrial;
    return useCallback((area, action, props = {}) => {
        const globalProps = userId
            ? {
                role: isDiscoverAdmin
                    ? "discover_admin"
                    : isDemoUser
                        ? "discover_demo"
                        : isViewerUser
                            ? "discover_viewer"
                            : undefined,
                isDemoUser,
                isFreeTrial,
                organizationId,
            }
            : {};
        analytics.track(buildEventName(area, action), Object.assign(Object.assign({}, globalProps), props));
    }, [analytics, userId, organizationId, isDemoUser, isViewerUser, isDiscoverAdmin, isFreeTrial]);
};
